import { differenceInDays } from 'date-fns/differenceInDays';
import { parse } from 'date-fns/parse';
import { addDays } from 'date-fns/addDays';
import { format, formatDate } from 'date-fns/format';
import { isBefore } from 'date-fns/isBefore';
import { isEqual } from 'date-fns/isEqual';
import { isWithinInterval } from 'date-fns/isWithinInterval';

export const dateFormat = 'dd-MMM-yyyy';

// =============================================================================
// V1: Maha Kumbh 2025 (Jan 11 - Mar 6, 2025)
// =============================================================================
export const offeringStartsFromV1 = parse('11-Jan-2025', dateFormat, new Date());
export const offeringEndsAtV1 = parse('06-Mar-2025', dateFormat, new Date());

export const bathingDatesV1 = [
  new Date('13 Jan 2025'),
  new Date('14 Jan 2025'),
  new Date('29 Jan 2025'),
  new Date('3 Feb 2025'),
  new Date('4 Feb 2025'),
  new Date('12 Feb 2025'),
  new Date('26 Feb 2025'),
];

// =============================================================================
// V2: Magh Mela 2026 (Dec 31 - Feb 28, 2026)
// =============================================================================
export const offeringStartsFromV2 = parse('31-Dec-2025', dateFormat, new Date());
export const offeringEndsAtV2 = parse('20-Feb-2026', dateFormat, new Date());

// Magh Mela 2026 Bathing Dates (Snan Dates) - 3-day windows around each significant date
export const bathingDatesV2: Date[] = [
  // Paush Purnima (Jan 3-5)
  new Date('3 Jan 2026'),
  // Makar Sankranti (Jan 14-16)
  new Date('14 Jan 2026'),
  // Mauni Amavasya (Jan 18-20)
  new Date('18 Jan 2026'),
  // Basant Panchami (Jan 23-25)
  new Date('23 Jan 2026'),
  // Maghi Purnima (Feb 1-3)
  new Date('1 Feb 2026'),
  // Maha Shivratri (Feb 15-17)
  new Date('15 Feb 2026'),
];

// =============================================================================
// ACTIVE VERSION: Points to the current offering
// =============================================================================
export const offeringStartsFrom = offeringStartsFromV2;
export const offeringEndsAt = offeringEndsAtV2;
export const bathingDates = bathingDatesV2;

const offeringInterval = {
  start: offeringStartsFrom,
  end: offeringEndsAt,
};

export const parseDate = (date: Date | string) =>
  date instanceof Date ? date : parse(date, dateFormat, new Date());

export const getDatesAvailable = (
  from: Date | string = offeringStartsFrom,
  to: Date | string = offeringEndsAt,
) => {
  const daysAvailable = differenceInDays(parseDate(to), parseDate(from)) + 1;

  return Array.from({ length: daysAvailable }, (_, i) => {
    const date = parseDate(from);
    return addDays(date, i);
  });
};

export const getDateString = (date: Date | string) => {
  return formatDate(date, dateFormat);
};

export const getBathingDaysInRangeInclusive = (startDate: string, endDate: string) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  return bathingDates.filter((date) => date >= start && date <= end);
};

export const getBathingDaysInRange = (startDate: string, endDate: string) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  return bathingDates.filter((date) => date >= start && date < end);
};

export const validateInRange = (date: Date) => {
  return date >= offeringStartsFrom && date <= offeringEndsAt;
};

export const validateInRangeFromString = (dateString: string) => {
  const date = parseDate(dateString);
  return date >= offeringStartsFrom && date <= offeringEndsAt;
};

export const isBathingDate = (date: Date | string) => {
  let parsedDate: Date;
  if (typeof date === 'string') {
    parsedDate = parseDate(date);
  } else {
    parsedDate = date;
  }

  return bathingDates.some((d) => d.toString() === parsedDate.toString());
};

export const hasValidDates = (startDate: string, endDate: string): boolean => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!isBefore(start, end)) {
    return false;
  }

  if (isEqual(start, end)) {
    return false;
  }

  if (!isWithinInterval(start, offeringInterval)) {
    return false;
  }

  if (!isWithinInterval(end, offeringInterval)) {
    return false;
  }

  // // We don't want to allow you to ever *end* on a bathing date.
  // if (isBathingDate(end)) {
  //   return false;
  // }

  return true;
};

export const hasValidBathingDates = (startDate: string, endDate: string): boolean => {
  if (!hasValidDates(startDate, endDate)) {
    return false;
  }

  // NOTE: as of Wed 4 Dec, we are allowing complete free reign of bookings, no minimum nights necessary.
  // NOTE: as of Fri 20 Dec, this has been re-introduced.
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  const bathingDatesInRange = getBathingDaysInRangeInclusive(startDate, endDate);
  if (bathingDatesInRange.length > 0 && differenceInDays(end, start) + 1 < 3) {
    return false;
  }

  return true;
};

export const displayDate = (dateString: string): string => {
  try {
    // Parse the date string into a Date object
    const parsedDate = parse(dateString, 'dd-MMM-yyyy', new Date());

    // Format the Date object into a human-readable format
    return format(parsedDate, 'do MMMM yyyy'); // Example: '20th January 2025'
  } catch (e) {
    return dateString;
  }
};
